import { OneSearchTileDataResponseWithId } from 'src/http/clients/onesearch/OneSearchGraphQL';
import { TranslationType } from 'src/util/i18n';
import { VissVehicleTileDataResponseWithId } from '../http/clients/viss/VissClientTeaser';
import { TPersonalizedVehicle } from '../schema/t4-personalization/PersonalizationData';
import {
  MixedDlsTeaserResponse,
  OptionalProductDto,
  OptionalProductDtoListWithId,
  ProductDto,
} from './dls/dls-response/ProductDto';
import {
  MixedDlsSearchAlternativeReponseWithId,
  SearchAlternativeReponseWithId,
  SearchResponseDTO,
} from './dls/dls-response/SearchResponseDto';
import {
  MixedOneSearchPreferenceResponse,
  OfferTeaserDto,
  OneSearchPreferenceResponse,
  OptionalOfferTeaserDto,
  OptionalOneSearchPreferenceResponseWithId,
} from './one-search/onesearch-response/OfferTeaserResponseDto';
import { MixedOneSearchGraphQlResponse } from './one-search/onesearch-response/OneSearchGraphQlResponse';
import {
  MixedOneSearchSearchResponse,
  OneSearchSearchResponse,
  OptionalOneSearchSearchResponseWithId,
  OptionalVehicleDto,
  VehicleDto,
} from './one-search/onesearch-response/SearchResponseDto';

export type ResponseTypes =
  | ProductDto
  | ProductDto[]
  | SearchResponseDTO
  | OneSearchPreferenceResponse
  | OneSearchSearchResponse;

export type ResponseDataTypes = OfferTeaserDto | VehicleDto | ProductDto;

export type T4ResponseType = {
  personalizedVehicle: TPersonalizedVehicle;
  responseTypeId: 'T4VehicleDto';
};

export type MixedT4Data = T4ResponseType & {
  market: string;
  lang: string;
  brand?: string;
  translations: (key: string, dataset?: TranslationType) => string;
};

export type OptionalResponseTypes =
  | OptionalResponseDataTypes
  | OptionalProductDto
  | OptionalOfferTeaserDto
  | OptionalProductDtoListWithId
  | OptionalOneSearchPreferenceResponseWithId
  | OptionalOneSearchSearchResponseWithId
  | OneSearchTileDataResponseWithId
  | VissVehicleTileDataResponseWithId;

export type OptionalResponseDataTypes =
  | OptionalOfferTeaserDto
  | OptionalVehicleDto
  | OptionalProductDto
  | T4ResponseType
  | SearchAlternativeReponseWithId;

export type MixedResponseType =
  | MixedOneSearchPreferenceResponse
  | MixedOneSearchSearchResponse
  | MixedOneSearchGraphQlResponse
  | MixedDlsTeaserResponse
  | MixedT4Data
  | MixedDlsSearchAlternativeReponseWithId
  | VissVehicleTileDataResponseWithId;

export function isT4Personalized(response: OptionalResponseTypes): response is T4ResponseType {
  return response.responseTypeId === 'T4VehicleDto';
}

export function isDlsTeaserList(response: OptionalResponseTypes): response is OptionalProductDtoListWithId {
  return response.responseTypeId === 'ProductDtoList';
}

export function isDlsTeaserResponse(response: OptionalResponseTypes): response is OptionalProductDto {
  return response.responseTypeId === 'ProductDto';
}

export function isOneSearchResponse(response: OptionalResponseTypes): response is OptionalOfferTeaserDto {
  return response.responseTypeId === 'OfferTeaserDto';
}

export function isDlsSearchAlternative(response: OptionalResponseTypes): response is SearchAlternativeReponseWithId {
  return response.responseTypeId === 'SearchResponseDTO';
}

export function isOneSearchPreferenceResponse(
  response: OptionalResponseTypes,
): response is OptionalOneSearchPreferenceResponseWithId {
  return response.responseTypeId === 'OneSearchPreferenceResponse';
}

export function isOneSearchGraphQlResponse(
  response: OptionalResponseTypes,
): response is OneSearchTileDataResponseWithId {
  return response.responseTypeId === 'OneSearchVehicleTileDataQuery';
}

export function isOneSearchSearchResponse(
  response: OptionalResponseTypes,
): response is OptionalOneSearchSearchResponseWithId {
  return response.responseTypeId === 'OneSearchSearchResponse';
}

export function isVissVehicleTileDataQueryResponse(
  response: OptionalResponseTypes,
): response is VissVehicleTileDataResponseWithId {
  return response.responseTypeId === 'VissVehicleTileDataQuery';
}

// optional response type dataguards
export function isMixedOneSearchSearchResponse(response: MixedResponseType): response is MixedOneSearchSearchResponse {
  return response.responseTypeId === 'VehicleDto';
}

export function isMixedPreferenceResponse(response: MixedResponseType): response is MixedOneSearchPreferenceResponse {
  return response.responseTypeId === 'OfferTeaserDto';
}

export function isMixedOneSearchGraphQlResponse(
  response: MixedResponseType,
): response is MixedOneSearchGraphQlResponse {
  return response.responseTypeId === 'OneSearchGraphQlVehicle';
}

export function isMixedDlsTeaserResponse(response: MixedResponseType): response is MixedDlsTeaserResponse {
  return response.responseTypeId === 'ProductDto';
}

export function isMixedT4Data(response: MixedResponseType): response is MixedT4Data {
  return response.responseTypeId === 'T4VehicleDto';
}

export function isMixedSearchAlternativeResponse(
  response: MixedResponseType,
): response is MixedDlsSearchAlternativeReponseWithId {
  return response.responseTypeId === 'SearchResponseDTO';
}
